import { Injectable } from '@angular/core';
import { LogService } from '../../../shared/services/log.service';
import { AccountService } from '../../../shared/services/account.service';
import { Router } from '@angular/router';
import { MAP_SETUP_PATH } from '../../../install/shared/navigation/constants';
import { RegistrationService } from '../../shared/services/registration.service';
import { Credentials } from '../../../shared/models/credentials';
import { CompleteState } from './complete_state';
import { RedirectService } from '../../../shared/services/redirect.service';
import { Angulartics2Facebook } from 'angulartics2/facebook';
import { Store } from '@ngrx/store';
import { updateWeakPassPopover } from './complete_actions_creator';
import { FlowTrackingService } from '../../shared/services/flow-tracking.service';
import { ENV } from '../../../environment';
import { MonitorService } from '../../../shared/services/monitor.service';
import { getPlatformByKey } from '../../../shared/services/constants/platform_types';
import { first, take } from 'rxjs/operators';
import { AnalyticsTrackingProvider, AuthService } from '@yotpo-ui/core';

@Injectable()
export class CompleteEffectsService {
  private completeState: CompleteState = null;
  constructor(private logger: LogService,
              private accountService: AccountService,
              private registrationService: RegistrationService,
              private authenticationService: AuthService,
              private redCrossService: AnalyticsTrackingProvider,
              private redirectService: RedirectService,
              private router: Router,
              private store: Store<CompleteState>,
              private fbAnalytics: Angulartics2Facebook,
              private flowTrackingService: FlowTrackingService,
              private monitorService: MonitorService
  ) {}

  /* We would like to avoid sending sign-up event for login-flow
   In this method:
   Loyalty flow - will always be sign-up
   Reviews flow - Can either be login or sign-up, depenes on context */

  completeOauthFlow(completeState: CompleteState): void {
    this.completeState = completeState;
    this.registrationService.completeOauthFlow(
      completeState.originalParams,
      completeState.newPassword,
      completeState.displayName
    ).pipe(take(1)).subscribe(async (response) => {
      if (response.error_password) {
        this.store.dispatch(updateWeakPassPopover({weakPassPopover: true}));
        this.handleError(response.error_password);
        return;
      }

      if (response.error_email) {
          this.handleError(response);
          return;
      }

      if (response.redirect_to) {
        try {
          if (response.context === 'sign-up') {
            await this.sendSignupCompletedTrackEvents();
          }
          this.redirectService.navigateOrRedirectTo(response.redirect_to);
          return;
        } catch (error) {
          this.handleError(error);
          return;
        }
      }

      if (!response.app_key && !response.utoken) {
        this.handleError('credentials are missing');
        return;
      }

      let credentials = new Credentials(response.app_key, response.utoken);
      this.authenticationService.setCredentials(credentials);

      this.accountService.completeRegistration(completeState.maxMonthlyOrders, completeState.phone)
        .pipe(take(1)).subscribe(() => {
        this.redCrossService.identify(() => {
          this.sendSignupCompletedTrackEvents();
        });
        this.router.navigateByUrl(MAP_SETUP_PATH);
      }, (errorResponse) => {
        this.handleError(errorResponse);
      });
    }, response => {
      this.handleError(response);
    });
  }

  private handleError(response) {
    this.logger.error(response);
    let error, url;
    if (response.error_website) {
      error = response.error_website;
    } else if (response.error_password) {
      error = response.error_password;
    } else if (response.error_email) {
      error = response.error_email;
    } else {
      error = 'General error';
    }
    this.monitorService.reportError(error, 'navigate to error page', 'signup', {email: response.email, context: 'completeOauthFlow'});
    url = this.buildErrorPageUrl(response, error);
    this.redirectService.navigateOrRedirectTo(url);
  }

  private async sendSignupCompletedTrackEvents() {
    await this.flowTrackingService.StepAppCompleted();
    await this.redCrossService.trackAsync('Signup Completed').pipe(first()).toPromise();
    this.fbAnalytics.eventTrack('CompleteRegistration');
  }

  private buildErrorPageUrl(response, error) {
    return ENV.ErrorPageUrl +
      this.router.createUrlTree(['/'], {
        queryParams: {
          product: response.product || this.completeState.originalParams['product'],
          email: response.email || this.completeState.originalParams['email'],
          context: response.context,
          platform: response.platform || this.getPlatformFromParams(this.completeState.originalParams),
          shop_domain: response.shop_domain || this.completeState.originalParams['shop_domain'],
          display_name: this.completeState.displayName,
          error: error.replace(/ /g, '_')
        }
      });
  }

  private getPlatformFromParams(params: any) {
    let platform_id = params['platform_id'];
    let platform = params['platform'];
    return getPlatformByKey('name', platform) || getPlatformByKey('id', platform_id);
  }
}
